/**
 * PATH: apps/quote/src/pages/SettingsPage.tsx
 * PURPOSE: Quote module settings
 */

import { useState } from 'react';
import { Save, Calculator, FileText, Bell, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getQuoteOverlay } from '@/lib/overlay/quoteOverlay';

export default function SettingsPage() {
  // Rate-profile defaults resolve through the tenant overlay seam (Track 2.1a-ii);
  // DEFAULTS are unchanged (30 / 10 / 35) until a tenant registers via setQuoteOverlay().
  const { rateProfile } = getQuoteOverlay();
  const [settings, setSettings] = useState({
    defaultValidityDays: rateProfile.defaultValidityDays,
    defaultGstRate: rateProfile.defaultGstRate,
    defaultMarginPercent: rateProfile.defaultMarginPercent,
    autoNumbering: true,
    quotePrefix: 'Q',
    includeGst: true,
    showMargin: false,
    emailNotifications: true,
    companyName: 'ABC Electrical Services',
    companyEmail: 'quotes@abcelectrical.com.au',
    companyPhone: '02 9123 4567',
  });

  const handleSave = () => {
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Configure quote defaults and preferences</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Save className="h-4 w-4" />
          Save Changes
        </button>
      </div>

      {/* Quote Defaults */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calculator className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Quote Defaults</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Validity (Days)
            </label>
            <input
              type="number"
              value={settings.defaultValidityDays}
              onChange={(e) => setSettings({ ...settings, defaultValidityDays: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default GST Rate (%)
            </label>
            <input
              type="number"
              value={settings.defaultGstRate}
              onChange={(e) => setSettings({ ...settings, defaultGstRate: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Quote Number Prefix
            </label>
            <input
              type="text"
              value={settings.quotePrefix}
              onChange={(e) => setSettings({ ...settings, quotePrefix: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Margin (%)
            </label>
            <input
              type="number"
              value={settings.defaultMarginPercent}
              onChange={(e) => setSettings({ ...settings, defaultMarginPercent: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Display Options */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Display Options</h2>
        </div>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Auto Numbering</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Automatically generate quote numbers</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, autoNumbering: !settings.autoNumbering })}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.autoNumbering ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.autoNumbering ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Include GST in Totals</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Show GST-inclusive totals by default</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, includeGst: !settings.includeGst })}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.includeGst ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.includeGst ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Show Margin Column</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Display margin % in quote builder</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, showMargin: !settings.showMargin })}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.showMargin ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.showMargin ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Company Information</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
            <input
              type="text"
              value={settings.companyName}
              onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={settings.companyEmail}
                onChange={(e) => setSettings({ ...settings, companyEmail: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input
                type="tel"
                value={settings.companyPhone}
                onChange={(e) => setSettings({ ...settings, companyPhone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

